import { Box, Skeleton, Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Section } from '../../ui/Section';

export interface ListPageSkeletonProps {
  showHeadline?: boolean;
  showFilters?: boolean;
  showQuickFilters?: boolean;
  showAdvancedFilter?: boolean;
  showTable?: boolean;
  loadingLabel?: string;
  sx?: SxProps<Theme>;
}

export const ListPageSkeleton = ({
  showHeadline = true,
  showFilters = true,
  showQuickFilters = false,
  showAdvancedFilter = false,
  showTable = true,
  loadingLabel,
  sx,
}: ListPageSkeletonProps) => {
  const { t } = useTranslation('common');

  return (
    <Box
      sx={sx}
      role="status"
      aria-busy="true"
      aria-label={loadingLabel ?? t('loading')}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            variant="text"
            width="min(70%, 420px)"
            height={48}
          />
          <Skeleton
            variant="text"
            width="min(90%, 620px)"
            height={28}
          />
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Skeleton
            variant="rounded"
            width={112}
            height={40}
          />
          <Skeleton
            variant="rounded"
            width={112}
            height={40}
          />
        </Stack>
      </Box>

      {showHeadline && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 6,
            mt: 6,
          }}
        >
          {Array.from({ length: 4 }, (_, index) => (
            <Box
              key={`headline-skeleton-${index}`}
              sx={{
                minWidth: 0,
                width: '100%',
                height: '100%',
              }}
            >
              <Section style={{ p: 4, mb: 0, height: '100%' }}>
                <Stack spacing={1}
                  aria-hidden="true">
                  <Skeleton variant="text"
                    width="55%"
                    height={20} />
                  <Skeleton variant="text"
                    width="45%"
                    height={36} />
                  <Skeleton variant="text"
                    width="75%"
                    height={20} />
                </Stack>
              </Section>
            </Box>
          ))}
        </Box>
      )}

      {showFilters && (
        <Box sx={{ mt: 6 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 3,
            }}
          >
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton
                key={`filter-skeleton-${index}`}
                variant="rounded"
                height={56}
              />
            ))}
          </Box>
          {showQuickFilters && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 3 }}
            >
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton
                  key={`quick-filter-skeleton-${index}`}
                  variant="rounded"
                  width={84}
                  height={32}
                />
              ))}
            </Stack>
          )}
          {showAdvancedFilter && (
            <Skeleton
              variant="rounded"
              width="100%"
              height={48}
              sx={{ mt: 3 }}
            />
          )}
        </Box>
      )}

      {showTable && (
        <Section style={{ mt: 6, p: 5, mb: 0 }}>
          <Stack spacing={2}>
            <Skeleton
              variant="text"
              width="30%"
              height={28}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={360}
            />
          </Stack>
        </Section>
      )}
    </Box>
  );
};
